import api from './api';
import { API_ENDPOINTS, API_BASE_URL } from '../config/api';

export interface Booking {
  _id?: string;
  id?: string;
  bookingCode: string;
  bookingType: 'hourly' | 'perPhoto';
  boothType: string;
  boothDisplayName?: string; // الاسم المعروض من لوحة التحكم
  customerName: string;
  phone: string;
  email: string;
  eventDate: string;
  eventTime: string;
  location: string;
  hours?: number;
  totalTickets?: number;
  freeTickets?: number;
  additionalTickets?: number;
  ticketPrice?: number;
  baseAmount: number;
  discount?: number;
  discountCode?: string;
  totalAmount: number;
  paidAmount?: number;
  city?: string;
  cityName?: string;
  cityFee?: number;
  eventType?: string; // نوع المناسبة
  paymentStatus: 'pending' | 'completed' | 'failed' | 'refunded';
  paymentMethod?: string;
  paymentId?: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  emailSent: boolean;
  ticketsEmailSent?: boolean;
  notes?: string;
  ticketDesign?: any;
  ticketsGenerated?: boolean;
  
  // Additional Tickets (purchased after initial booking)
  additionalTicketsCount?: number;
  additionalTicketsPurchased?: Array<{
    ticketNumber: number;
    qrCode: string;
    paymentId: string;
    purchaseDate: string;
    used: boolean;
    usedAt?: string;
    usedBy?: string;
  }>;
  ticketUrls?: Array<{
    ticketNumber: number;
    url: string;
    publicId: string;
    downloadUrl: string;
  }>;
  createdAt?: string;
  updatedAt?: string;
}

export interface BookingStats {
  totalBookings: number;
  confirmedBookings: number;
  todayBookings: number;
  totalRevenue: number;
  monthlyRevenue: number;
}

export interface Ticket {
  _id?: string;
  id?: string;
  bookingId: string;
  ticketNumber: number;
  qrCode: string;
  type: 'free' | 'additional';
  used: boolean;
  usedAt?: string;
  usedBy?: string;
}

class BookingService {
  // Create new booking
  async createBooking(bookingData: Partial<Booking>): Promise<Booking> {
    try {
      console.log('Sending booking request to:', `${API_BASE_URL}${API_ENDPOINTS.BOOKINGS}`);
      console.log('Booking data:', bookingData);
      
      const response = await api.post<{ success: boolean; booking: Booking }>(
        API_ENDPOINTS.BOOKINGS,
        bookingData
      );
      return response.data.booking;
    } catch (error: any) {
      console.error('Create booking error:', error.response?.data || error);
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        errors: error.response?.data?.errors,
        message: error.message,
        code: error.code
      });
      throw new Error(error.response?.data?.message || error.response?.data?.errors?.[0]?.msg || 'فشل إنشاء الحجز');
    }
  }

  // Get all bookings
  async getBookings(filters?: {
    status?: string;
    paymentStatus?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
  }): Promise<Booking[]> {
    try {
      const response = await api.get<{ success: boolean; bookings: Booking[] }>(
        API_ENDPOINTS.BOOKINGS,
        { params: filters }
      );
      
      // Normalize booking IDs
      return response.data.bookings.map(booking => ({
        ...booking,
        id: booking._id || booking.id
      }));
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'فشل جلب الحجوزات');
    }
  }

  // Get single booking
  async getBooking(id: string): Promise<{ booking: Booking; tickets: Ticket[] }> {
    try {
      const response = await api.get<{ 
        success: boolean; 
        booking: Booking; 
        tickets: Ticket[] 
      }>(`${API_ENDPOINTS.BOOKINGS}/${id}`);
      
      return {
        booking: {
          ...response.data.booking,
          id: response.data.booking._id || response.data.booking.id
        },
        tickets: response.data.tickets
      };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'فشل جلب بيانات الحجز');
    }
  }

  // Update booking
  async updateBooking(id: string, updates: Partial<Booking>): Promise<Booking> {
    try {
      const response = await api.put<{ success: boolean; booking: Booking }>(
        `${API_ENDPOINTS.BOOKINGS}/${id}`,
        updates
      );
      return response.data.booking;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'فشل تحديث الحجز');
    }
  }

  // Delete booking
  async deleteBooking(id: string): Promise<void> {
    try {
      await api.delete(`${API_ENDPOINTS.BOOKINGS}/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'فشل حذف الحجز');
    }
  }

  // Get booking statistics
  async getBookingStats(): Promise<BookingStats> {
    try {
      const response = await api.get<{ success: boolean; stats: BookingStats }>(
        API_ENDPOINTS.BOOKING_STATS
      );
      return response.data.stats;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'فشل جلب الإحصائيات');
    }
  }

  // Get confirmed bookings
  async getConfirmedBookings(): Promise<Booking[]> {
    return this.getBookings({ paymentStatus: 'completed' });
  }

  // Update payment status
  async updatePaymentStatus(
    id: string, 
    status: string, 
    paymentId?: string
  ): Promise<Booking> {
    return this.updateBooking(id, {
      paymentStatus: status as any,
      paymentId,
      status: status === 'completed' ? 'confirmed' : 'pending'
    });
  }
}

export const bookingServiceAPI = new BookingService();